import React, {
  Dispatch,
  DispatchWithoutAction,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { BearerTokenProvider } from "sonobello.utilities.react.axios";
import { Nullable, Optional } from "sonobello.utilities.react.mui";

import { ContactResult } from "./dtos/ContactResult";
import { DisqualifyingReason } from "./dtos/DisqualifyingReason";
import { EnhancedToken, Token } from "./dtos/Token";
import { ValidationProgress } from "./types/ValidationProgress";
import { ViewConnector } from "./types/ViewConnector";
import { LocalStorageConfigs, saveToLocal } from "./utils/LocalStorage";
import IBookingController from "./V2/Calendar/Types/IBookingController";
import { IFlow } from "./V2/Flows/Types/IFlow";
import IPathObserver from "./V2/Routing/Types/IPathObserver";
import IMedicalConclusions from "./V2/Steps/Medical/Types/IMedicalConclusions";
import IMedicalQualification from "./V2/Steps/Medical/Types/IMedicalQualification";
import { IPatient } from "./V2/Types/Entities/Patient";
import ICenter from "./V2/Types/ICenter";
import IReservation from "./V2/Types/IReservation";
import ISession from "./V2/Types/ISession";

export interface AppContextProps extends IPathObserver {
  /** The token provider to be used for all authorized useApi calls. */
  bearerTokenProvider: BearerTokenProvider;
  /** {@inheritdoc IBookingController} */
  bookingController: Nullable<IBookingController>;
  /** Personal information about the current user. */
  patient: IPatient;
  /** The current contact validation state held by the user. */
  contactInfoValidatedCState: ValidationProgress;
  /** The default center to be used to book appointments. */
  defaultCenter: ICenter;
  /** The reasons for which the current user may be considered a non-candidate. */
  disqualifyingReasonsCState?: DisqualifyingReason[];
  /** The unique identifier of the contact associated with the current OBX session. */
  contactId: string;
  /** The contact result for the session. */
  contactResult: Nullable<ContactResult>;
  /** The current flow that is assigned to the session. */
  flowCState: IFlow;
  /** The unique identifier of the flow associated with the current OBX session. */
  flowIdCState: string;
  /** A flag indicating if the device information for this local instance has been recorded. */
  isDeviceLoggedCState: boolean;
  /** The state of the medical form answers given by the user. */
  medicalForm: IMedicalQualification;
  /** Conclusions drawn from the selection of medical form answers. */
  medicalConclusions: IMedicalConclusions;
  /** The authorization token that should be refreshed. */
  refreshTokenCState?: Token;
  /** The current appointment reservation held by the client. */
  reservation: Nullable<IReservation>;
  /** The router which allows child components to change the route of the application. */
  router: ViewConnector;
  /** A state which mutates when the patient note should be saved. */
  savePatientNoteTrigger: unknown;
  /** The url link key value which identifies the OBX session. */
  sessionKeyCState: string;
  /** The security token containing keys used to authorize requests to the backend. */
  tokenCState?: EnhancedToken;
  /** State setter for the currently selected center's calendar. */
  setBookingController: Dispatch<SetStateAction<Nullable<IBookingController>>>;
  /** State setter for the application patient. */
  setPatient: Dispatch<SetStateAction<IPatient>>;
  /** State setter for the contact validation state. */
  setContactValidationCState: Dispatch<SetStateAction<ValidationProgress>>;
  /** State setter for the disqualifying reasons state. */
  setDisqualifyingReasonsCState: Dispatch<SetStateAction<Optional<DisqualifyingReason[]>>>;
  /** State setter for the device info recorded flag. */
  setIsDeviceLoggedCState: Dispatch<SetStateAction<boolean>>;
  /** State setter for the session's contact id. */
  setContactId: Dispatch<SetStateAction<string>>;
  /** State setter for the contact result. */
  setContactResult: Dispatch<ContactResult>;
  /** State setter for the session's flow id. */
  setFlowIdCState: Dispatch<SetStateAction<string>>;
  /** State setter for the medical form conclusions. */
  setMedicalConclusions: Dispatch<SetStateAction<IMedicalConclusions>>;
  /** State setter for the medical form. */
  setMedicalForm: Dispatch<SetStateAction<IMedicalQualification>>;
  /** State setter for the session's appointment reservation. */
  setReservation: Dispatch<SetStateAction<Nullable<IReservation>>>;
  /** State setter for the session's authorization token. */
  setTokenCState: Dispatch<SetStateAction<Optional<EnhancedToken>>>;

  /** Trigger to save the patient note when changed. */
  triggerSavePatientNote: DispatchWithoutAction;
}

export interface IAppContextProviderProps extends IPathObserver {
  /** The flow experienced by the user. */
  flow: IFlow;
  /** The initial state of the session data that should be provided to the children of the provider. */
  session: ISession;
  /** The connector which allows for the alteration of the user's route. */
  viewConnector: ViewConnector;
}
export const ProtectedAppContextProvider = ({
  flow,
  pathName,
  session,
  viewConnector,
  children
}: PropsWithChildren<IAppContextProviderProps>): React.JSX.Element => {
  // Center
  const [defaultCenter] = useState(session.defaultCenter);
  useEffect(() => defaultCenter.cache(), [defaultCenter]);

  // Contact Info Validated
  const [contactInfoValidatedCState, setContactValidationCState] = useState(ValidationProgress.Unvalidated);

  // Booking Schedule
  const [bookingController, setBookingController] = useState<Nullable<IBookingController>>(null);

  // Patient
  const [patient, setPatient] = useState(session.patient);
  useEffect(() => saveToLocal(patient, LocalStorageConfigs.patient.key), [patient]);

  const [savePatientNoteTrigger, setSavePatientNoteTrigger] = useState({});

  // Device Info Recorded Flag
  const [isDeviceLoggedCState, setIsDeviceLoggedCState] = useState(session.isDeviceLogged);
  useEffect(() => saveToLocal(isDeviceLoggedCState, LocalStorageConfigs.isDeviceLogged.key), [isDeviceLoggedCState]);

  const [disqualifyingReasonsCState, setDisqualifyingReasonsCState] = useState(session.disqualifyingReasons);
  useEffect(
    () => saveToLocal(disqualifyingReasonsCState, LocalStorageConfigs.disqualifyingReasons.key),
    [disqualifyingReasonsCState]
  );

  // Contact Id
  const [contactId, setContactId] = useState(session.contactId);
  useEffect(() => localStorage.setItem(LocalStorageConfigs.contactId.key, contactId), [contactId]);

  // Contact Result
  const [contactResult, setContactResult] = useState(session.contactResult);
  useEffect(() => saveToLocal(contactResult, LocalStorageConfigs.contactResult.key), [contactResult]);

  // Flow Id
  const [flowIdCState, setFlowIdCState] = useState(session.flowId);
  useEffect(() => localStorage.setItem(LocalStorageConfigs.flowId.key, flowIdCState), [flowIdCState]);

  const [medicalConclusions, setMedicalConclusions] = useState(session.medicalConclusions);
  useEffect(() => saveToLocal(medicalConclusions, LocalStorageConfigs.medicalConclusions.key), [medicalConclusions]);

  // Medical Form
  const [medicalForm, setMedicalForm] = useState(session.medicalForm || {});
  useEffect(() => saveToLocal(medicalForm, LocalStorageConfigs.medicalForm.key), [medicalForm]);

  // Authorization Token
  const [tokenCState, setTokenCState] = useState<Optional<EnhancedToken>>(session.token);
  const tokenRef = useRef(tokenCState);
  const [refreshTokenCState, setRefreshTokenCState] = useState<Token>();
  const refreshToken = useCallback(
    () => setRefreshTokenCState(t => (t?.token !== tokenRef.current?.token ? tokenRef.current : t)),
    []
  );
  const [bearerTokenProvider] = useState(new BearerTokenProvider(tokenCState?.token || "", refreshToken));
  useEffect(() => {
    tokenRef.current = tokenCState;
    if (tokenCState) {
      saveToLocal(EnhancedToken.ToBase(tokenCState), LocalStorageConfigs.token.key);
      viewConnector.setToken(tokenCState.token);
      bearerTokenProvider.updateTokenAndRetryUnauthorizedRequests(tokenCState.token);
    }
  }, [tokenCState]);

  const [reservation, setReservation] = useState(session.reservation);
  const reservationRef = useRef(reservation);
  useEffect(() => {
    if (reservation) reservation.cache();
    else if (reservationRef.current) reservationRef.current.unsetCache();
    reservationRef.current = reservation;
  }, [reservation]);

  // Url Session Key
  const [sessionKeyCState] = useState(session.key);
  useEffect(() => {
    if (sessionKeyCState) localStorage.setItem(LocalStorageConfigs.sessionKey.key, sessionKeyCState);
  }, [sessionKeyCState]);

  useEffect(() => {
    localStorage.setItem(LocalStorageConfigs.flow.key, flow.name);
    viewConnector.setFlowId(flowIdCState);
    viewConnector.setContactId(contactId);
  }, []);

  useEffect(() => localStorage.setItem(LocalStorageConfigs.version.key, session.version!), [session.version]);

  return (
    <AppContext.Provider
      value={{
        bearerTokenProvider,
        bookingController,
        contactInfoValidatedCState,
        patient,
        defaultCenter,
        disqualifyingReasonsCState,
        flowCState: flow,
        flowIdCState,
        isDeviceLoggedCState,
        contactId,
        contactResult,
        medicalConclusions,
        medicalForm,
        pathName,
        refreshTokenCState,
        reservation,
        router: viewConnector,
        savePatientNoteTrigger,
        sessionKeyCState,
        tokenCState,
        setIsDeviceLoggedCState,
        setBookingController,
        setContactValidationCState,
        setPatient,
        setDisqualifyingReasonsCState,
        setContactId,
        setContactResult,
        setFlowIdCState,
        setMedicalConclusions,
        setMedicalForm,
        setReservation,
        setTokenCState,
        triggerSavePatientNote: () => setSavePatientNoteTrigger({})
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const AppContext = createContext({} as AppContextProps);

export default AppContext;
